import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useMobileVerifyContext } from '../../context/MobileVerifyContext';
import { MobileVerifyResponseType } from '../useMobileVerify';
import { savePhoneNumber, SavePhoneNumberResponse } from '../../helpers/savePhoneNumber';
import { Tracker, trackingContext, formAction } from '@jbhi-fi/components/src/Tracking';
import CustomerHelper from '../../helpers/CustomerHelper';
import { useVerifyLayout } from '../useVerifyLayout/useVerifyLayout';
import { pageThemeEnum } from '../useVerifyLayout/helpers';
import { useCustomerApi } from '../customerApi';
import { PhoneHelper } from '../../../../../utils/phoneHelper/PhoneHelper';

const customerHelper = new CustomerHelper();

export interface IuseSetMobileProps {
  handleRedirect: (data: MobileVerifyResponseType) => void;
  tracker: Tracker;
  receiptPhone?: string;
  receiptCode?: string;
  suppressTracking?: boolean;
}

export type useSetMobileType = {
  mobileInput: string;
  setMobileInput: Dispatch<SetStateAction<string>>;
  isMobileValid: boolean;
  setIsMobileValid: Dispatch<SetStateAction<boolean>>;
  saveError: { heading: string; content?: string } | undefined;
  setSaveError: Dispatch<SetStateAction<{ heading: string; content?: string } | undefined>>;
  submitting: boolean;
  setSubmitting: Dispatch<SetStateAction<boolean>>;
  handleSave: () => Promise<SavePhoneNumberResponse>;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const useSetMobile = ({ handleRedirect, tracker, suppressTracking }: IuseSetMobileProps): useSetMobileType => {
  const { setMobileVerifyData } = useMobileVerifyContext();
  const [mobileInput, setMobileInput] = useState<string>('');
  const [isMobileValid, setIsMobileValid] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<{ heading: string; content?: string } | undefined>(undefined);
  const { themeContext, setThemeContext, pageTheme } = useVerifyLayout();
  const { customer } = useCustomerApi();
  const { receiptPhone, receiptCode } = themeContext ?? {};
  const { birthMonth } = customer ?? {};

  const [submitting, setSubmitting] = useState<boolean>(
    Boolean(
      (receiptCode && pageTheme.current === pageThemeEnum.perks && birthMonth) ||
        (receiptCode && pageTheme.current !== pageThemeEnum.perks)
    )
  );

  useEffect(() => {
    if (submitting) setSaveError(undefined);
  }, [submitting]);

  useEffect(() => {
    const saveWithReceiptCode = async () => {
      if (
        (receiptCode && pageTheme.current === pageThemeEnum.perks && birthMonth) ||
        (receiptCode && pageTheme.current !== pageThemeEnum.perks)
      ) {
        setSubmitting(true);
        const { ok, response } = await handleSave();
        setSubmitting(false);
        if (ok && response) {
          setThemeContext(themeContext => ({ ...themeContext, isPerksFinished: true }));
          handleRedirect(response);
        }
      }
    };
    saveWithReceiptCode();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const hasError = event.target.value.length > 0 && !PhoneHelper.validateWithRegex(event.target.value);
    setIsMobileValid(!hasError);
    setMobileInput(event.target.value.trim().replace(/\D/, ''));
  };

  const handleSave = async (): Promise<SavePhoneNumberResponse> => {
    if (!suppressTracking) {
      tracker.trackClick({ label: 'verify mobile number' });
    }
    setSubmitting(true);

    const customerId = window.Jbhifi?.shopifyCustomerId;
    const mobileNumber = isMobileValid ? customerHelper.formatMobileNumber(mobileInput) : '';

    const response = await savePhoneNumber({
      customerId,
      mobileNumber,
      receiptPhone,
      receiptCode,
      setMobileVerifyData,
    });
    if (!response?.ok) {
      if (!suppressTracking) {
        tracker.trackError({ errorMsg: response?.error?.heading, contextOverride: trackingContext.form });
      }
      setSubmitting(false);
      setSaveError(response?.error);
    } else {
      if (!suppressTracking) {
        tracker.trackFormEvent({ label: 'phone number', action: formAction.fieldComplete });
      }
    }
    return response;
  };

  return {
    mobileInput,
    setMobileInput,
    isMobileValid,
    setIsMobileValid,
    saveError,
    setSaveError,
    submitting,
    setSubmitting,
    handleSave,
    handleChange,
  };
};
